<template>
    <dynamic-element
        v-if="!isDisabled"
        :id="renderId"
        v-bind="renderProps"
        :tag="tagNew"
        :class="renderClasses"
        :style="renderStyles"
        v-on="$listeners"
        @click="onClick"
        @mouseenter="onMouseenter"
        @mouseleave="onMouseleave"
        @submit="onSubmit"
        @change="onChange"
    >
        <slot />
    </dynamic-element>
</template>

<script>
import { mapState } from 'vuex';

import DynamicElement from '@/components/utilities/dynamic-element';
import flexEventMixin from '@/mixins/flex-event';
import { STATE_CLASSES } from '@/data/constants/classes';
import { getClassDefinition } from '@/utils/tailwind-helper';
import {
    backgroundImageStyle,
    gradientStyleByElement,
    floatingNavStyle, // only used on xccom
    offsetStyle,
    textShadowStyle,
} from '@/utils/css';

export default {
    components: { DynamicElement },
    mixins: [flexEventMixin],

    props: {
        tag: {
            type: String,
            default: 'div',
        },

        // changes the root element to an anchor, and adds link-related options
        isLinkDefault: {
            type: Boolean,
            default: false,
        },

        // the flex class group(s) you'd like to prevent from being applied on the element
        // e.g. pass ['textColor'] to prevent any color classes (f-text-black, f-text-white, etc) from being applied
        // see @/data/tailwind.js for more details about flex class groups
        excludeClasses: {
            type: [Array, String],
            default: () => [],
        },

    },

    data() {
        return {
            tagNew: this.tag,
        };
    },

    computed: {
        ...mapState({
            settings: state => state.settings,
        }),

        htmlClass() {
            let classes = this.$parent.htmlClass;

            if (Array.isArray(this.excludeClasses)) {
                // exclude only certain classes
                const disabledFlexClassGroups = [].concat(this.excludeClasses);

                classes = classes.filter((className) => {
                    const classDefinition = getClassDefinition(className);
                    const { tailwindDefinitionProp } = classDefinition;

                    return !disabledFlexClassGroups.includes(tailwindDefinitionProp);
                });
            } else if (typeof this.excludeClasses === 'string') {
                // n.b. Vue passes an empty string when no prop value is set
                // exclude all classes
                classes = [];
            }

            return classes;
        },

        stateClasses() {
            return {
                [STATE_CLASSES.DISABLED]: this.isDisabled,
            };
        },

        renderId() {
            return this.$parent.idOverride || this.$parent.id;
        },

        isDisabled() {
            return this.$parent.hasElementData('isDisabled');
        },

        renderProps() {
            const linkProps = this.$parent.isLink
                ? {
                    href: this.$parent.renderHref,
                    target: this.$parent.renderTarget,
                    title: this.$parent.title,
                    rel: this.$parent.renderRel,
                    targetItemId: this.$parent.renderTargetItemId,
                }
                : {};

            const ariaLabelProp = this.$parent.ariaLabel ? { 'aria-label': this.$parent.ariaLabel } : '';
            const ariaLabelledByProp = this.$parent.ariaLabelledBy ? { 'aria-labelledby': this.$parent.ariaLabelledBy } : '';
            const ariaHiddenProp = this.$parent.ariaHidden ? { 'aria-hidden': true } : '';
            const accessibilityRole = this.$parent.accessibilityRole ? { role: this.$parent.accessibilityRole } : '';

            return { ...linkProps, ...this.$parent.htmlCustomAttributes, ...ariaLabelProp, ...ariaLabelledByProp, ...ariaHiddenProp, ...accessibilityRole };
        },

        renderClasses() {
            return [this.htmlClass, this.stateClasses];
        },

        renderStyles() {
            return {
                ...backgroundImageStyle(this.$parent),
                ...gradientStyleByElement(this.$parent),
                ...floatingNavStyle(this.$parent),
                ...offsetStyle(this.$parent),
                ...textShadowStyle(this.$parent),
            };
        },
    },

    beforeMount() {
        if (this.isLinkDefault) this.$parent.isLink = true;
        if (this.$parent.isLink) {
            this.setAsLink();
        }
    },

    mounted() {
        if (this.$parent.isLink) {
            if (!this.$parent.$el.classList.contains('f-block')) {
                this.$parent.$el.classList.add('f-block');
            }
        }
    },

    methods: {
        setAsLink() {
            this.tagNew = 'a';
        },
    },
};
</script>
